import React, { useEffect, useState } from 'react'
import { useMediaQuery, useTheme, Container, Box, Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import Carousel from '../LandingPage/Carousel'
import {
  TextColumnGroup,
  TextColumnGroupVariant,
} from '../../types/heroTextSection'
import TextColumnInsert from './TextColumnInsert'

interface TextColumnGroupProps {
  textColumnInsert: TextColumnGroup
}

const responsiveSettings = [
  {
    breakpoint: 1432,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 760,
    settings: {
      slidesToShow: 1,
    },
  },
]

type ToggleBtnProps = {
  isExpanded: boolean
  setIsExpanded: (val: boolean) => void
  count: number
}

export const ToggleBtn: React.FC<ToggleBtnProps> = ({
  isExpanded,
  setIsExpanded,
  count,
}) => {
  const { classes } = useStyles()
  return (
    <div className={classes.expandButton}>
      <button
        className={classes.seeAllButton}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <>
            See Less
            <KeyboardArrowUp className={classes.iconSmallPadding} />
          </>
        ) : (
          <>
            See All {count}
            <KeyboardArrowDown className={classes.iconSmallPadding} />
          </>
        )}
      </button>
    </div>
  )
}

export const TextColumnGroupInsert: React.FC<TextColumnGroupProps> = ({
  textColumnInsert,
}) => {
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isExpanded, setIsExpanded] = useState(!isMobile)

  useEffect(() => {
    setIsExpanded(!isMobile)
  }, [isMobile])
  switch (textColumnInsert.variant) {
    case TextColumnGroupVariant.Columns:
      return (
        <Container
          className={cx(classes.container, classes.groupContainer)}
          disableGutters
        >
          {textColumnInsert.textColumnArray.map((textColumn) => (
            <TextColumnInsert
              textColumnInsert={textColumn}
              key={textColumn.contentful_id}
            />
          ))}
        </Container>
      )
    case TextColumnGroupVariant.ColumnsSeeMore:
      const displayTextColumns =
        isExpanded || !isMobile
          ? textColumnInsert.textColumnArray
          : textColumnInsert.textColumnArray.slice(0, 3)

      return (
        <>
          <Container
            className={cx(classes.container, classes.groupContainer)}
            disableGutters
          >
            {displayTextColumns.map((textColumn) => (
              <TextColumnInsert
                textColumnInsert={textColumn}
                key={textColumn.contentful_id}
              />
            ))}
          </Container>
          {isMobile && textColumnInsert.textColumnArray.length > 3 && (
            <ToggleBtn
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
              count={6}
            />
          )}
        </>
      )
    case TextColumnGroupVariant.Columns4:
      return (
        <Container
          className={cx(classes.container, classes.noWrapGroupContainer)}
          disableGutters
        >
          {textColumnInsert.textColumnArray.map((textColumn) => (
            <TextColumnInsert
              textColumnInsert={textColumn}
              containerClass={classes.noMargin}
              key={textColumn.contentful_id}
            />
          ))}
        </Container>
      )
    case TextColumnGroupVariant.Carousel:
      return (
        <Box className={classes.carouselContainer}>
          <Carousel
            slidesToShow={4}
            dots={false}
            arrowContainerLeftClass={classes.arrowContainerLeft}
            arrowContainerRightClass={classes.arrowContainerRight}
            responsiveSettings={responsiveSettings}
          >
            {textColumnInsert.textColumnArray.map((textColumn) => (
              <TextColumnInsert
                textColumnInsert={textColumn}
                key={textColumn.contentful_id}
              />
            ))}
          </Carousel>
        </Box>
      )
    case TextColumnGroupVariant.ColumnsMobile2:
      return (
        <Container className={cx(classes.noWrapGroupContainer)} disableGutters>
          <Grid container className={classes.paddingBottom}>
            {textColumnInsert.textColumnArray.map((textColumn) => (
              <Grid item xs={6} sm={3} key={textColumn.contentful_id}>
                <TextColumnInsert
                  textColumnInsert={textColumn}
                  containerClass={classes.noMargin}
                  key={textColumn.contentful_id}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      )
    case TextColumnGroupVariant.Masonry:
      return (
        <Container
          className={cx(classes.container, classes.groupContainer)}
          disableGutters
        >
          {textColumnInsert.textColumnArray.map((textColumn) => (
            <TextColumnInsert
              textColumnInsert={textColumn}
              key={textColumn.contentful_id}
            />
          ))}
        </Container>
      )
    default:
      return null
  }
}

const useStyles = makeStyles()((theme) => ({
  groupContainer: {
    width: '100%',
    flexWrap: 'wrap',
  },
  noWrapGroupContainer: {
    width: '100%',
    maxWidth: '100%',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('lg')]: {
      flexWrap: 'wrap',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    rowGap: '2rem',
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      justifyContent: 'space-around',
    },
    alignItems: 'baseline',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      rowGap: '0',
    },
  },
  carouselContainer: {
    width: '100%',
    marginTop: '2.9rem',
    [theme.breakpoints.down('md')]: {
      marginTop: '1rem',
    },
  },
  arrowContainerLeft: {
    '&&': {
      left: '-17rem',
      [theme.breakpoints.down('md')]: {
        zIndex: 10,
        backgroundColor: 'white',
        left: '2rem',
        top: '9rem',
      },
      '@media (min-width: 1456px)': {
        display: 'none !important',
      },
      '&:hover': {
        backgroundColor: `${theme.palette.action.hover}`,
        border: 'none',
        '& > * ': {
          color: 'white !important',
        },
      },
    },
  },
  arrowContainerRight: {
    '&&': {
      right: '5rem',
      top: '10rem',
      backgroundColor: 'white',
      [theme.breakpoints.down('md')]: {
        right: '2rem',
        top: '9rem',
      },

      '&:hover': {
        backgroundColor: `${theme.palette.action.hover}`,
        border: 'none',
        '& > * ': {
          color: 'white !important',
        },
      },
    },
  },
  noMargin: {
    margin: '0',
  },
  paddingBottom: {
    paddingBottom: '2rem',
  },
  expandButton: {
    textAlign: 'center',
    marginTop: '10px',
    borderTop: '1px solid #e7e7e7',
    width: '100%',
    paddingTop: '16px',
  },
  seeAllButton: {
    border: 'none',
    background: 'transparent',
    color: '#5A6377',
    fontSize: '14px',
  },
  iconSmallPadding: {
    fontSize: 'medium',
    position: 'relative',
    top: '3px',
  },
}))
export default TextColumnGroupInsert
